import React, { useMemo, useRef, Fragment, useCallback } from 'react';
import { type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import memoizeOne from 'memoize-one';
import { graphql } from 'relay-runtime';
import Page from '@atlaskit/page';
import { Box, xcss } from '@atlaskit/primitives';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { isMobileAndInMvpOrExistingUsersExperiment } from '@atlassian/jira-mobile-web/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { RelayMigrationProvider } from '@atlassian/jira-relay-apollo-migration-utils/src/services/use-query/index.tsx';
import { SoftwareWorkSuggestionsYourWorkEntryPoint } from '@atlassian/jira-software-work-suggestions-your-work/entrypoint.tsx';
import type { JiraHomeWithWorkSuggestionsQuery } from '@atlassian/jira-relay/src/__generated__/JiraHomeWithWorkSuggestionsQuery.graphql.ts';
import { jiraHomeWorkSuggestionsIsDeveloperResource } from '@atlassian/jira-router-resources-jira-home-your-work-work-suggestions/src/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import {
	STARRED_TAB_KEY,
	VIEWED_TAB_KEY,
	WORK_SUGGESTIONS_TAB_KEY,
} from '../common/constants/tab-content.tsx';
import type { TabConfig } from '../common/types/tabs.tsx';
import { LastVisitedTabProviderFF } from '../services/last-visited-tab-provider/index.tsx';
import { GlobalPagesDocumentTitle } from './document-title/index.tsx';
import FullSkeleton from './full-skeleton/index.tsx';
import Header from './header/index.tsx';
import messages from './messages.tsx';
import RecentProjects from './recent-projects/index.tsx';
import { RightPanelLayout } from './right-panel-layout/index.tsx';
import { TabContainerFF } from './tab-container/tab-container-ff.tsx';
import { getTabConfigToUse, getWorkSuggestionsTab, servicesNav4 } from './utils.tsx';

import { type Props, JiraHome } from './main.tsx';

const onTabSelected = memoizeOne(
	(setLastVisitedTab) => (selectedTab: string) => setLastVisitedTab(selectedTab),
);

export const JiraHomeWithWorkSuggestionsWithRef = ({
	queryReference,
	prefetchResult,
}: Props & {
	queryReference: PreloadedQuery<JiraHomeWithWorkSuggestionsQuery>;
}): React.JSX.Element => {
	const workSuggestionsQuery = usePreloadedQuery(
		graphql`
			query JiraHomeWithWorkSuggestionsQuery($cloudId: ID!, $isSoftwareLicensed: Boolean!) {
				workSuggestions @optIn(to: "WorkSuggestions") @include(if: $isSoftwareLicensed) {
					userProfileByCloudId(cloudId: $cloudId) @optIn(to: "WorkSuggestionsUserProfile") {
						persona
					}
				}
			}
		`,
		queryReference,
	);
	const isMobileWebAndInMVPExperiment = isMobileAndInMvpOrExistingUsersExperiment();
	const isWorkSuggestionsEnabled =
		workSuggestionsQuery?.workSuggestions?.userProfileByCloudId?.persona === 'DEVELOPER' &&
		!isMobileWebAndInMVPExperiment;
	const intl = useIntl();
	const hasLoadedWorkSuggestions = useRef<boolean>(false);
	const {
		entryPointActions: entryPointWorkSuggestionsActions,
		entryPointReferenceSubject: entryPointWorkSuggestionsReferenceSubject,
	} = useEntryPoint(SoftwareWorkSuggestionsYourWorkEntryPoint, {});
	const loadWorkSuggestions = useCallback(() => {
		if (!hasLoadedWorkSuggestions.current) {
			entryPointWorkSuggestionsActions.load();
			hasLoadedWorkSuggestions.current = true;
		}
	}, [entryPointWorkSuggestionsActions]);

	const tabConfigsToUse = useMemo(() => {
		if (isWorkSuggestionsEnabled) {
			return [
				...getTabConfigToUse(),
				getWorkSuggestionsTab(entryPointWorkSuggestionsReferenceSubject, loadWorkSuggestions),
			];
		}

		return getTabConfigToUse();
	}, [isWorkSuggestionsEnabled, entryPointWorkSuggestionsReferenceSubject, loadWorkSuggestions]);

	const intlTabConfigs: TabConfig[] = useMemo(
		() =>
			tabConfigsToUse.map((tab) => {
				const config: TabConfig = {
					...tab,
					id: tab.id,
					// generate an intld label for the Atlaskit Tab Navigation component
					// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ dashboards: MessageDescriptorV2; projects: MessageDescriptorV2; experimentPreferenceChangeSuccessMessage: MessageDescriptorV2; ... 6 more ...; title: MessageDescriptorV2; }'.
					label: intl.formatMessage(messages[tab.id]),
				};

				return config;
			}),
		[intl, tabConfigsToUse],
	);

	const slowRunOnce = useRef(false);

	// eslint-disable-next-line jira/ff/no-preconditioning
	if (fg('uip_slow_test') && !slowRunOnce.current) {
		slowRunOnce.current = true;
		let timeNow = 0;
		let timeDelayForExperiment = 0;

		try {
			timeNow = performance.now();
			timeDelayForExperiment = expVal('uip_slow_test_experiment', 'delay', 0);
		} catch (e) {
			// Silently fail, we have safe defaults above if we fail to get them
		}

		if (timeDelayForExperiment > 0 && timeDelayForExperiment < 1000) {
			/* eslint-disable-next-line no-empty */
			while (performance.now() - timeNow <= timeDelayForExperiment) {}
		}
	}

	const RightPanelWrapper = isMobileWebAndInMVPExperiment ? Fragment : RightPanelLayout;

	return (
		<RelayMigrationProvider>
			<ContextualAnalyticsData sourceType={SCREEN} sourceName="jiraHome">
				<ReportErrors id="root" packageName="jiraHome">
					<RightPanelWrapper>
						<div data-testid="global-pages.home.ui.home">
							<GlobalPagesDocumentTitle data-testid="global-pages.home.ui.global-pages-document-title" />
							<Box xcss={PageWrapperStyles}>
								<Page>
									<FireScreenAnalytics />
									<Header />
									{/* @ts-expect-error Type 'Partial<RouteResourceResponse<PrefetchResult>>' is not assignable to type 'RouteResourceResponse<PrefetchResult> | undefined'. */}
									<LastVisitedTabProviderFF prefetchResult={prefetchResult}>
										{({ queriedTab, lastVisitedTab, setLastVisitedTab }) => {
											let matchingConfigs = intlTabConfigs.filter(
												(config) => config.id === lastVisitedTab,
											);
											switch (queriedTab) {
												case 'starred':
													matchingConfigs = intlTabConfigs.filter(
														(config) => config.id === STARRED_TAB_KEY,
													);
													break;
												case 'viewed':
													matchingConfigs = intlTabConfigs.filter(
														(config) => config.id === VIEWED_TAB_KEY,
													);
													break;
												default:
													break;
											}
											if (
												matchingConfigs.length === 0 &&
												prefetchResult.loading &&
												!isMobileWebAndInMVPExperiment
											) {
												return (
													<FullSkeleton
														tabConfigs={intlTabConfigs}
														deepDiveCards={<RecentProjects prefetchResult={undefined} />}
													/>
												);
											}

											const selectedTab =
												matchingConfigs.length > 0 ? matchingConfigs[0] : intlTabConfigs[0];
											const lastVisitedTabIsWorkSuggestions =
												selectedTab.id === WORK_SUGGESTIONS_TAB_KEY;

											lastVisitedTabIsWorkSuggestions && loadWorkSuggestions();

											return (
												<>
													{!isMobileWebAndInMVPExperiment && (
														<RecentProjects
															// @ts-expect-error Type 'Partial<RouteResourceResponse<PrefetchResult>>' is not assignable to type 'RouteResourceResponse<PrefetchResult> | undefined'.
															prefetchResult={prefetchResult}
														/>
													)}
													<TabContainerFF
														services={servicesNav4}
														// @ts-expect-error - TS2322 - Type 'Partial<RouteResourceResponse<PrefetchResult>>' is not assignable to type 'RouteResourceResponse<PrefetchResult>'.
														prefetchResult={prefetchResult}
														tabConfigs={intlTabConfigs}
														defaultSelected={selectedTab}
														onTabChange={onTabSelected(setLastVisitedTab)}
													/>
												</>
											);
										}}
									</LastVisitedTabProviderFF>
								</Page>
							</Box>
						</div>
					</RightPanelWrapper>
				</ReportErrors>
			</ContextualAnalyticsData>
		</RelayMigrationProvider>
	);
};

export const JiraHomeWithWorkSuggestions = (props: Props): React.JSX.Element => {
	const { queryReference } = useRelayResource(jiraHomeWorkSuggestionsIsDeveloperResource);
	return (
		<Placeholder name="jira-home-with-work-suggestions" fallback={<JiraHome {...props} />}>
			{queryReference ? (
				<JiraHomeWithWorkSuggestionsWithRef queryReference={queryReference} {...props} />
			) : (
				<JiraHome {...props} />
			)}
		</Placeholder>
	);
};

const PageWrapperStyles = xcss({
	minWidth: '329px',
	marginTop: 'space.075',
	marginRight: 'space.100',
	marginBottom: 'space.400',
	marginLeft: 'space.100',
});
